import React from 'react';
import { MeetingRoom } from './meetingRoomTypes';

interface MeetingRoomComponentProps {
  meetingRoom: MeetingRoom;
  isSelected: boolean;
  onSelect: (meetingRoom: MeetingRoom) => void;
}

export const MeetingRoomComponent = React.memo(({ 
  meetingRoom, 
  isSelected, 
  onSelect
}: MeetingRoomComponentProps) => {
  const [isHovering, setIsHovering] = React.useState(false);

  const handleMouseEnter = () => {
    if (meetingRoom.isAvailable) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (meetingRoom.isAvailable) {
      setIsHovering(false);
    }
  };

  const handleClick = () => {
    if (meetingRoom.isAvailable) {
      onSelect(meetingRoom);
    }
  };

  // Color logic - match seat colors exactly
  const getFillColor = () => {
    if (isSelected) return '#4caf50'; // Green fill when selected (same as seats)
    if (!meetingRoom.isAvailable) return '#bdbdbd'; // Unavailable (gray)
    if (isHovering) return '#E8DCC0'; // Hover color (slightly darker room background)
    return '#F1ECDC'; // Available color (same as cabin room background)
  };

  const getStrokeColor = () => {
    if (isSelected) return '#2e7d32'; // Dark green border when selected (same as seats)
    if (!meetingRoom.isAvailable) return '#757575';
    return '#000000'; // Black border for available state
  };

  return (
    <g>
      {/* Meeting room area */}
      <rect
        x={meetingRoom.x}
        y={meetingRoom.y}
        width={meetingRoom.width}
        height={meetingRoom.height}
        fill={getFillColor()}
        stroke={getStrokeColor()}
        strokeWidth={isSelected ? 3 : 1}
        shapeRendering="crispEdges"
        opacity={isSelected ? 1 : (meetingRoom.isAvailable ? 0.3 : 0.5)}
        style={{
          transition: 'all 0.2s ease',
          cursor: meetingRoom.isAvailable ? 'pointer' : 'not-allowed',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
        aria-label={`Meeting Room ${meetingRoom.name}, ${meetingRoom.isAvailable ? 'Available' : 'Unavailable'}`}
        role="button"
        tabIndex={meetingRoom.isAvailable ? 0 : -1}
        onKeyDown={(e) => {
          if (meetingRoom.isAvailable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <title>
          {`Meeting Room: ${meetingRoom.name}\nStatus: ${meetingRoom.isAvailable ? 'Available' : 'Unavailable'}${isSelected ? '\n✓ Selected' : ''}`}
        </title>
      </rect>
      
      {/* Meeting room label */}
      <text
        x={meetingRoom.x + meetingRoom.width / 2}
        y={meetingRoom.y + meetingRoom.height / 2 - 10}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="600"
        fill={isSelected ? '#000000' : '#666'}
        className="pointer-events-none select-none"
      >
        {meetingRoom.name}
      </text>
      
      {/* Meeting Room subtitle */}
      <text
        x={meetingRoom.x + meetingRoom.width / 2}
        y={meetingRoom.y + meetingRoom.height / 2 + 5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="10"
        fontWeight="400"
        fill={isSelected ? '#000000' : '#666'}
        className="pointer-events-none select-none"
      >
        Meeting Room
      </text>
      
      {/* Status indicator */}
      <text
        x={meetingRoom.x + meetingRoom.width / 2}
        y={meetingRoom.y + meetingRoom.height / 2 + 20}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="8"
        fontWeight="500"
        fill={isSelected ? '#000000' : '#666'}
        className="pointer-events-none select-none"
      >
        {meetingRoom.isAvailable ? 'Available' : 'Unavailable'}
      </text>
    </g>
  );
});

MeetingRoomComponent.displayName = 'MeetingRoomComponent';